import { can, isStaff, type Capability, type UserRole } from '@jalupro/shared';
import { isModuleEnabled, type ModuleKey } from '../modules';

/**
 * Sidebar navigation models for the two portals, re-pointed at the real PATH
 * routes (single host: /agent/*, /admin/*).
 *
 * Items are grouped the way the operation works (JP-CRM Area 11 §2.4): the
 * sidebar renders a small group label whenever `group` changes between two
 * consecutive visible items.
 *
 * `cap` (when present) gates an item to the roles holding that capability in the
 * EFFECTIVE permission matrix — evaluated at render time, so a Super Admin's edit of
 * the matrix is reflected on the next render. `module` hides the item while its
 * module is switched off in modules.ts. Entries are absent, not greyed.
 */
export interface NavItem {
  /** Stable id (used to derive the active item from the current route). */
  readonly id: string;
  /** English label (fallback / reference). The sidebar renders `labelKey` via i18n. */
  readonly label: string;
  /** Key into the common i18n dictionary (see i18n/common.ts) for the localized label. */
  readonly labelKey: string;
  /** Key into the common i18n dictionary for the group heading this item sits under. */
  readonly group: string;
  /** Absolute path the item navigates to. */
  readonly to: string;
  /** Icon key (see components/NavIcon.tsx). */
  readonly icon?: string;
  /** Capability a role must hold to see this item. Omitted ⇒ every role in the portal. */
  readonly cap?: Capability;
  /** Functional module the item belongs to (hidden while the module is off). */
  readonly module?: ModuleKey;
  /** Whether matching the active route should be exact (e.g. the admin dashboard at /admin). */
  readonly end?: boolean;
}

/** Agent portal navigation (paths under /agent). */
export const AGENT_NAV: readonly NavItem[] = [
  { id: 'dash', label: 'Home', labelKey: 'nav_home', group: 'nav_group_my_work', icon: 'home', to: '/agent/dashboard', module: 'agentPortal' },
  { id: 'new', label: 'New order', labelKey: 'nav_new_order', group: 'nav_group_my_work', icon: 'plus', to: '/agent/orders/new', cap: 'orders.create', module: 'agentPortal' },
  { id: 'orders', label: 'My orders', labelKey: 'nav_my_orders', group: 'nav_group_my_work', icon: 'orders', to: '/agent/orders', end: true, cap: 'orders.list', module: 'agentPortal' },
  { id: 'clients', label: 'My clients', labelKey: 'nav_my_clients', group: 'nav_group_my_work', icon: 'clients', to: '/agent/clients', cap: 'clients.view', module: 'clients' },
  { id: 'products', label: 'Products', labelKey: 'nav_products', group: 'nav_group_reference', icon: 'products', to: '/agent/products', cap: 'products.view', module: 'agentPortal' },
  { id: 'settings', label: 'Settings', labelKey: 'nav_settings', group: 'nav_group_reference', icon: 'settings', to: '/agent/settings' },
];

/**
 * Admin portal navigation (paths under /admin). Shared by every staff role; each item is
 * gated by the capability behind the screen it opens and by its module flag.
 */
export const ADMIN_NAV: readonly NavItem[] = [
  // The admin dashboard exists for staff with dashboard.volume; the Warehouse has no
  // dashboard until the pick-list screen exists (its home is the catalog).
  { id: 'dash', label: 'Dashboard', labelKey: 'nav_dashboard', group: 'nav_group_control', icon: 'dashboard', to: '/admin', end: true, cap: 'dashboard.volume', module: 'dashboard' },
  { id: 'orders', label: 'Orders', labelKey: 'nav_orders', group: 'nav_group_operations', icon: 'orders', to: '/admin/orders', cap: 'orders.list', module: 'orders' },
  // Catalog — prices are shown only to roles with products.prices.
  { id: 'products', label: 'Products', labelKey: 'nav_products', group: 'nav_group_operations', icon: 'products', to: '/admin/products', cap: 'products.view', module: 'products' },
  // Payments (bank review queue) stays off until the bank integration is live.
  { id: 'clients', label: 'Clients', labelKey: 'nav_clients', group: 'nav_group_market', icon: 'clients', to: '/admin/clients', cap: 'clients.view', module: 'clients' },
  { id: 'agents', label: 'Agents', labelKey: 'nav_agents', group: 'nav_group_market', icon: 'agents', to: '/admin/agents', cap: 'agents.list', module: 'agents' },
  // The screen is the FINANCIAL report set today (finance KPIs, exports with amounts).
  { id: 'reports', label: 'Reports', labelKey: 'nav_reports', group: 'nav_group_market', icon: 'reports', to: '/admin/reports', cap: 'reports.moneyOps', module: 'reports' },
  { id: 'users', label: 'Users', labelKey: 'nav_users', group: 'nav_group_admin', icon: 'users', to: '/admin/users', cap: 'users.manage', module: 'users' },
  { id: 'settings', label: 'Settings', labelKey: 'nav_settings', group: 'nav_group_admin', icon: 'settings', to: '/admin/settings', cap: 'settings.manage', module: 'settings' },
  // Own account (2FA, password) — every admin-portal role, no capability needed.
  { id: 'profile', label: 'Profile', labelKey: 'nav_profile', group: 'nav_group_account', icon: 'profile', to: '/admin/profile' },
];

/** Filter a nav model down to the items a given role may see (effective matrix + module flags). */
export function navForRole(items: readonly NavItem[], role: UserRole): NavItem[] {
  return items.filter((it) => isModuleEnabled(it.module) && (!it.cap || can(role, it.cap)));
}

/** Staff roles only (admin portal): drop items whose capability the role lacks. */
export function adminNavForRole(role: UserRole): NavItem[] {
  return isStaff(role) ? navForRole(ADMIN_NAV, role) : [];
}

/** The first page a role can open in its portal (used as the portal home). */
export function firstNavPath(items: readonly NavItem[], role: UserRole, fallback: string): string {
  return navForRole(items, role)[0]?.to ?? fallback;
}
